import React, {useState, useEffect, useCallback, useRef, useMemo} from 'react';

import {X, Shield, Lock, PlayCircle, Info} from 'lucide-react';

import {trapFocus} from '../Utils/Accessibility';
import {
    createDefaultConsent,
    readStoredConsent,
    writeConsent,
    resetConsent,
    COOKIE_CONSENT_VERSION,
} from '../Utils/CookieConsent';
import HtmlRenderer from '../Utils/HtmlRenderer';

import {CookieConsent, CookieCategory} from '../declarations';

/* -------------------- Constants -------------------- */
const BANNER_DELAY_MS = 600;

/* -------------------- CookieBanner -------------------- */
const CookieBanner = () => {
    const [visible, setVisible] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [consent, setConsent] = useState<CookieConsent>(createDefaultConsent);
    const containerRef = useRef<HTMLDivElement>(null);

    /* -------------------- Initialization -------------------- */
    useEffect(() => {
        const stored = readStoredConsent();
        if (stored) {
            setConsent(stored);
            return;
        }
        const timerId = setTimeout(() => setVisible(true), BANNER_DELAY_MS);
        return () => clearTimeout(timerId);
    }, []);

    /* -------------------- Focus Trap -------------------- */
    useEffect(() => {
        if (!visible || !containerRef.current) return;
        return trapFocus(containerRef.current);
    }, [visible]);

    /* -------------------- Persistence Helpers -------------------- */
    const persistConsent = useCallback(
        (overrides: Partial<CookieConsent>) => {
            const next: CookieConsent = {
                ...consent,
                ...overrides,
                timestamp: Date.now(),
                version: COOKIE_CONSENT_VERSION,
            };
            writeConsent(next);
            setConsent(next);
            setVisible(false);
        },
        [consent],
    );

    const acceptAll = useCallback(
        () => persistConsent({necessary: true, media: true}),
        [persistConsent],
    );
    const acceptNecessary = useCallback(
        () => persistConsent({necessary: true, media: false}),
        [persistConsent],
    );
    const savePreferences = useCallback(
        () => persistConsent({}),
        [persistConsent],
    );
    const resetAll = useCallback(() => {
        resetConsent();
        setConsent(createDefaultConsent());
        setVisible(true);
    }, []);

    /* -------------------- Cookie Categories -------------------- */
    const categories: CookieCategory[] = useMemo(
        () => [
            {
                key: 'necessary',
                icon: <Lock size={15} />,
                title: 'Necessary',
                description: 'Required for basic functions',
                checked: true,
                disabled: true,
                color: 'from-blue-400/15 to-blue-600/15',
                details:
                    'Essential for the website to function properly. Includes session management and security features from our hosting provider Cloudflare. These cookies do not require your consent.',
            },
            {
                key: 'media',
                icon: <PlayCircle size={15} />,
                title: 'Media',
                description: 'External content (YouTube)',
                checked: consent.media,
                color: 'from-pink-400/15 to-pink-600/15',
                details:
                    'We embed YouTube videos on our site. If you accept, YouTube may set cookies for video playback and analytics. The videos are only loaded with your consent.',
            },
        ],
        [consent.media],
    );

    if (!visible) return null;

    return (
        <div
            className='fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-end justify-center md:items-end md:justify-end p-3 sm:p-5 animate-fade-in'
            role='dialog'
            aria-modal='true'
            aria-labelledby='cookie-banner-title'
        >
            <div
                ref={containerRef}
                className='bg-[rgb(var(--bg-primary))] rounded-lg shadow-2xl w-full max-w-sm sm:max-w-md overflow-hidden border border-[rgb(var(--border))] animate-slide-up'
                style={{maxHeight: '85dvh'}}
            >
                {/* Header */}
                <div className='sticky top-0 bg-[rgb(var(--bg-card))] flex items-center justify-between px-5 pt-5 pb-3 border-b border-[rgb(var(--border))] z-10'>
                    <div className='flex items-center gap-2.5'>
                        <Shield
                            size={20}
                            className='text-[rgb(var(--accent-primary))]'
                            aria-hidden='true'
                        />
                        <div>
                            <h2
                                id='cookie-banner-title'
                                className='text-base font-bold leading-tight text-[rgb(var(--text-primary))]'
                            >
                                Cookie Settings
                            </h2>
                            <p className='text-xs text-[rgb(var(--text-secondary))]'>
                                Manage your preferences
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={acceptNecessary}
                        className='p-1.5 rounded-lg hover:bg-[rgb(var(--bg-secondary))] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--accent-primary))]'
                        aria-label='Close and accept necessary cookies only'
                    >
                        <X size={18} aria-hidden='true' />
                    </button>
                </div>

                {/* Body */}
                <div
                    className='overflow-y-auto px-5 py-4 space-y-4'
                    style={{maxHeight: 'calc(85dvh - 130px)'}}
                >
                    <div className='text-xs text-[rgb(var(--text-secondary))] leading-relaxed'>
                        <HtmlRenderer html="We use cookies to enhance your experience. With your consent we may load YouTube videos, which can set cookies. Our hosting provider (Cloudflare) only sets technically necessary cookies for security and performance. We do not use tracking or analytics cookies.<br />See our <a href='/privacy' class='text-[rgb(var(--accent-primary))] hover:underline font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--accent-primary))] rounded'>Privacy Policy</a>." />
                    </div>

                    <button
                        onClick={() => setShowDetails((v) => !v)}
                        className='flex items-center gap-1.5 text-xs text-[rgb(var(--text-secondary))] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--accent-primary))] rounded'
                        aria-expanded={showDetails}
                        aria-controls='cookie-details'
                    >
                        <Info size={13} aria-hidden='true' />
                        {showDetails ? 'Hide details' : 'Show details'}
                    </button>

                    <div id='cookie-details' className='space-y-2.5'>
                        {categories.map((cat) => (
                            <div
                                key={cat.key}
                                className={`p-3 rounded-lg bg-gradient-to-r ${cat.color} border border-[rgb(var(--border))]`}
                            >
                                <div className='flex items-center justify-between'>
                                    <div className='flex items-center gap-2.5'>
                                        <div
                                            className='p-1.5 rounded-lg bg-white/40 dark:bg-black/20'
                                            aria-hidden='true'
                                        >
                                            {cat.icon}
                                        </div>
                                        <div>
                                            <p className='text-sm font-medium text-[rgb(var(--text-primary))]'>
                                                {cat.title}
                                            </p>
                                            <HtmlRenderer
                                                html={cat.description}
                                                tag='p'
                                                className='text-xs text-[rgb(var(--text-secondary))]'
                                            />
                                        </div>
                                    </div>
                                    {cat.disabled ? (
                                        <span className='text-xs px-2 py-1 rounded-full bg-[rgb(var(--accent-primary))]/10 text-[rgb(var(--accent-primary))] font-medium whitespace-nowrap'>
                                            Always on
                                        </span>
                                    ) : (
                                        <label className='relative inline-flex items-center cursor-pointer flex-shrink-0'>
                                            <input
                                                type='checkbox'
                                                className='sr-only peer'
                                                checked={cat.checked}
                                                aria-label={`Enable ${cat.title} cookies`}
                                                onChange={(e) =>
                                                    setConsent((prev) => ({
                                                        ...prev,
                                                        [cat.key]:
                                                            e.target.checked,
                                                    }))
                                                }
                                            />
                                            <div className='w-10 h-5 bg-[rgb(var(--border))] rounded-full peer-checked:bg-[rgb(var(--accent-primary))] transition-colors' />
                                            <div className='absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition-transform peer-checked:translate-x-5 shadow-sm' />
                                        </label>
                                    )}
                                </div>
                                {showDetails && (
                                    <HtmlRenderer
                                        html={cat.details}
                                        tag='p'
                                        className='text-xs text-[rgb(var(--text-secondary))] mt-2 leading-relaxed'
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Footer Actions */}
                <div className='px-5 pb-4 pt-3 border-t border-[rgb(var(--border))] space-y-2.5'>
                    <div className='grid grid-cols-3 gap-2'>
                        <button
                            onClick={acceptNecessary}
                            className='btn-secondary text-xs py-2 px-2 truncate'
                        >
                            Necessary
                        </button>
                        <button
                            onClick={savePreferences}
                            className='btn-secondary text-xs py-2 px-2 truncate'
                        >
                            Save
                        </button>
                        <button
                            onClick={acceptAll}
                            className='btn-primary text-xs py-2 px-2 truncate'
                        >
                            Accept All
                        </button>
                    </div>
                    <div className='flex justify-between items-center'>
                        <button
                            onClick={resetAll}
                            className='text-xs text-[rgb(var(--text-secondary))] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--accent-primary))] rounded'
                        >
                            Reset all
                        </button>
                        <a
                            href='/privacy'
                            className='text-xs text-[rgb(var(--accent-primary))] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--accent-primary))] rounded'
                        >
                            Privacy Policy
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

CookieBanner.displayName = 'CookieBanner';
export default React.memo(CookieBanner);
